import { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Bell, AlarmClock, Check, X, ChevronDown } from 'lucide-react';
import { apiGet, apiPost } from '../lib/api';
import { useToast } from '../contexts/ToastContext';
import { isPushSupported, subscribeToPush, isSubscribedToPush } from '../lib/pushNotifications';
import { getEntityUrl } from '../lib/entityUrls';

const SNOOZE_OPTIONS = [5, 10, 15, 30, 60, 120, 240, 480] as const;

function formatSnooze(m: number) {
  return m < 60 ? `${m} min` : `${m / 60} hour${m > 60 ? 's' : ''}`;
}

interface Reminder {
  id: string;
  title: string;
  remind_at: string;
  status: string;
  entity_type: string | null;
  entity_id: string | null;
}

export function NotificationCenter() {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [upcoming, setUpcoming] = useState<Reminder[]>([]);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushSupported] = useState(isPushSupported);
  const ref = useRef<HTMLDivElement>(null);

  // Poll upcoming count every 30s
  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 30_000);
    return () => clearInterval(interval);
  }, []);

  // Check push subscription status
  useEffect(() => {
    isSubscribedToPush().then(setPushEnabled);
  }, []);

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  async function fetchCount() {
    try {
      const { count: c } = await apiGet<{ count: number }>('/reminders/upcoming-count');
      setCount(c);
    } catch { /* ignore */ }
  }

  async function loadUpcoming() {
    try {
      const fired = await apiGet<Reminder[]>('/reminders?status=fired');
      setUpcoming(fired.sort((a, b) => a.remind_at.localeCompare(b.remind_at)).slice(0, 8));
    } catch { /* ignore */ }
  }

  function handleToggle() {
    if (!open) loadUpcoming();
    setOpen(!open);
  }

  async function handleEnablePush() {
    const result = await subscribeToPush();
    if (result) {
      setPushEnabled(true);
      toast.success('Push notifications enabled');
      // Send test notification
      try { await apiPost('/push/test', {}); } catch { /* ignore */ }
    } else {
      toast.error('Could not enable push notifications');
    }
  }

  const [snoozeOpenId, setSnoozeOpenId] = useState<string | null>(null);
  const [snoozePos, setSnoozePos] = useState<{ top: number; left: number } | null>(null);

  const openSnoozeMenu = useCallback((id: string, buttonEl: HTMLButtonElement) => {
    if (snoozeOpenId === id) {
      setSnoozeOpenId(null);
      return;
    }
    const rect = buttonEl.getBoundingClientRect();
    // Position above the button, right-aligned
    setSnoozePos({ top: rect.top, left: rect.right });
    setSnoozeOpenId(id);
  }, [snoozeOpenId]);

  // Close snooze menu on outside click
  useEffect(() => {
    if (!snoozeOpenId) return;
    function handleClick() { setSnoozeOpenId(null); }
    // Delay to avoid closing from the click that opened it
    const timer = setTimeout(() => document.addEventListener('mousedown', handleClick), 0);
    return () => { clearTimeout(timer); document.removeEventListener('mousedown', handleClick); };
  }, [snoozeOpenId]);

  async function handleSnooze(id: string, minutes: number) {
    try {
      await apiPost(`/reminders/${id}/snooze`, { minutes });
      setSnoozeOpenId(null);
      loadUpcoming();
      fetchCount();
    } catch { toast.error('Failed to snooze'); }
  }

  async function handleComplete(id: string) {
    try {
      await apiPost(`/reminders/${id}/complete`, {});
      loadUpcoming();
      fetchCount();
    } catch { toast.error('Failed to complete'); }
  }

  async function handleDismiss(id: string) {
    try {
      await apiPost(`/reminders/${id}/dismiss`, {});
      loadUpcoming();
      fetchCount();
    } catch { toast.error('Failed to dismiss'); }
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={handleToggle}
        className="relative p-2 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors"
      >
        <Bell size={18} />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 flex items-center justify-center px-1 text-[10px] font-bold bg-accent text-white rounded-full">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <>
        {/* Mobile backdrop — catches taps to close */}
        <div className="fixed inset-0 z-40 sm:hidden" onClick={() => setOpen(false)} />
        <div className="fixed left-3 right-3 top-[calc(52px+env(safe-area-inset-top)+8px)] max-h-[calc(100vh-80px)] sm:absolute sm:left-auto sm:right-0 sm:top-10 sm:w-80 sm:max-h-[400px] z-50 bg-bg-surface border border-border rounded-lg shadow-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <span className="text-xs font-semibold text-text-primary uppercase tracking-wider">Reminders</span>
            <Link
              to="/reminders"
              onClick={() => setOpen(false)}
              className="text-xs text-accent hover:text-accent-hover transition-colors"
            >
              View all
            </Link>
          </div>

          {/* Push notification enable banner */}
          {pushSupported && !pushEnabled && (
            <div className="px-4 py-2 bg-accent/5 border-b border-border">
              <button
                onClick={handleEnablePush}
                className="text-xs text-accent hover:text-accent-hover font-medium"
              >
                Enable push notifications
              </button>
              <p className="text-[10px] text-text-muted mt-0.5">Get notified even when Dossier is closed</p>
            </div>
          )}

          <div className="max-h-80 overflow-y-auto">
            {upcoming.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Bell size={24} className="mx-auto text-text-muted opacity-40 mb-2" />
                <p className="text-xs text-text-muted">No notifications</p>
              </div>
            ) : (
              upcoming.map(r => {
                const timeStr = new Date(r.remind_at).toLocaleString(undefined, {
                  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
                });
                const link = r.entity_type && r.entity_id
                  ? getEntityUrl({ entity_type: r.entity_type, entity_id: r.entity_id, name: '' })
                  : null;
                return (
                  <div key={r.id} className="flex items-center gap-2 px-4 py-2.5 hover:bg-bg-hover transition-colors group">
                    <div className="flex-1 min-w-0">
                      {link ? (
                        <Link to={link} onClick={() => setOpen(false)} className="block text-xs font-medium text-text-primary truncate hover:text-accent transition-colors">
                          {r.title}
                        </Link>
                      ) : (
                        <p className="text-xs font-medium text-text-primary truncate">{r.title}</p>
                      )}
                      <p className="text-[10px] text-text-muted">{timeStr}</p>
                    </div>
                    <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-all">
                      <div className="flex">
                        <button onClick={() => handleSnooze(r.id, 15)} className="p-0.5 text-text-muted hover:text-warning" title="Snooze 15m">
                          <AlarmClock size={12} />
                        </button>
                        <button onClick={(e) => openSnoozeMenu(r.id, e.currentTarget)} className="p-0.5 -ml-0.5 text-text-muted hover:text-warning" title="Snooze options">
                          <ChevronDown size={8} />
                        </button>
                      </div>
                      <button onClick={() => handleComplete(r.id)} className="p-0.5 text-text-muted hover:text-success" title="Complete">
                        <Check size={12} />
                      </button>
                      <button onClick={() => handleDismiss(r.id)} className="p-0.5 text-text-muted hover:text-text-primary" title="Dismiss">
                        <X size={12} />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
        </>
      )}

      {/* Snooze duration menu — rendered via portal to escape overflow clipping */}
      {snoozeOpenId && snoozePos && createPortal(
        <div
          className="fixed z-[100] bg-bg-surface border border-border rounded-md shadow-lg py-1 w-24"
          style={{ top: snoozePos.top, left: snoozePos.left - 96 }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {SNOOZE_OPTIONS.map(m => (
            <button
              key={m}
              onClick={() => handleSnooze(snoozeOpenId, m)}
              className="w-full px-3 py-1 text-[10px] text-text-secondary hover:bg-bg-hover text-left"
            >
              {formatSnooze(m)}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}
